/**
 * Тарифы (ADMIN_SPEC §3.6, design Handoff 3/4). Оркестратор: активные версии
 * (мок-запрос), режимы правки Соло/Клиника, подтверждение новой версии,
 * история и тост. Раскладка — десктоп (инлайн-правка) / мобилка (bottom-sheet)
 * через useBreakpoint. Изменения применяются со следующего продления; версии
 * не удаляются.
 */

import {useState} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';

import {
  AFFECTED_ON_SAVE,
  type SaveClinicInput,
  saveClinicTariff,
  type SaveSoloInput,
  saveSoloTariff,
  tariffsQueryOptions,
} from '@services/tariffs';
import {apiEnabled} from '@services/utils';
import {keys} from '@queries/keys';
import {useBreakpoint} from '@hooks/useBreakpoint';
import {useToast} from '@hooks/useToast';
import {Toast} from '@components/Toast';

import {ConfirmVersionModal} from './ConfirmVersionModal';
import {TariffHistoryDrawer} from './TariffHistoryDrawer';
import {TariffsDesktop} from './TariffsDesktop';
import {TariffsMobile} from './TariffsMobile';

type Editing = 'solo' | 'clinic' | null;
type Pending = {kind: 'solo'; input: SaveSoloInput} | {kind: 'clinic'; input: SaveClinicInput};

export const Tariffs = () => {
  const {isMobile} = useBreakpoint();
  const qc = useQueryClient();
  const {data, isLoading} = useQuery(tariffsQueryOptions());
  const {toast, notify} = useToast();

  const [editing, setEditing] = useState<Editing>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  const soloMut = useMutation({mutationFn: saveSoloTariff});
  const clinicMut = useMutation({mutationFn: saveClinicTariff});
  const saving = soloMut.isPending || clinicMut.isPending;

  const confirmSave = () => {
    if (!pending) return;
    const finish = (message: string) => {
      qc.invalidateQueries({queryKey: keys.tariffs.all});
      setPending(null);
      setEditing(null);
      notify(message, 2600);
    };
    const fail = () => notify('Не удалось сохранить: проверьте пакеты и попробуйте ещё раз', 2600);
    if (pending.kind === 'solo') {
      soloMut.mutate(pending.input, {
        onSuccess: (r) => finish(`Соло-тариф v${r.version} сохранён`),
        onError: fail,
      });
    } else {
      clinicMut.mutate(pending.input, {
        onSuccess: (r) =>
          finish(
            r.affectedClients === null
              ? `Тариф клиник v${r.version} сохранён`
              : `Тариф клиник v${r.version} сохранён · затронет клиник: ${r.affectedClients}`,
          ),
        onError: fail,
      });
    }
  };

  const layoutProps = {
    data,
    isLoading,
    editing,
    onEditSolo: () => setEditing('solo'),
    onEditClinic: () => setEditing('clinic'),
    onCancel: () => setEditing(null),
    onAskSaveSolo: (price: number) => setPending({kind: 'solo', input: {price}}),
    onAskSaveClinic: (input: SaveClinicInput) => setPending({kind: 'clinic', input}),
    onOpenHistory: () => setHistoryOpen(true),
    onNotify: (message: string) => notify(message, 2400),
  };

  return (
    <>
      {isMobile ? <TariffsMobile {...layoutProps} /> : <TariffsDesktop {...layoutProps} />}

      <TariffHistoryDrawer
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        items={data?.history ?? []}
        mobile={isMobile}
      />
      <ConfirmVersionModal
        open={pending !== null}
        onOpenChange={(o) => !o && !saving && setPending(null)}
        affected={apiEnabled ? null : pending ? AFFECTED_ON_SAVE[pending.kind] : 0}
        pending={saving}
        mobile={isMobile}
        onConfirm={confirmSave}
      />
      <Toast message={toast} />
    </>
  );
};
